import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail, Phone, X, Inbox, Loader2, AlertCircle, ImageIcon, Camera, Download, Send, Trash2,
} from 'lucide-react';
import { withAdminAuth } from '../../lib/adminAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_FILTERS = [
  { key: 'new', label: 'New' },
  { key: 'replied', label: 'Replied' },
  { key: 'closed', label: 'Closed' },
  { key: 'all', label: 'All' },
];

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const STATUS_BADGE = {
  new: 'bg-amber-100 text-amber-800 border-amber-200',
  replied: 'bg-blue-100 text-blue-800 border-blue-200',
  closed: 'bg-gray-100 text-gray-700 border-gray-200',
};

const authHeader = () => ({});  // legacy hook — auth via httpOnly cookie

// ────────────────────────────────────────────────────────────────────────────
// Photo thumbnail with auth-fetched blob URL (private endpoint requires Bearer)
// ────────────────────────────────────────────────────────────────────────────
const PrivatePhoto = ({ src, alt, className = '' }) => {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!src) return;
    let revoked = false;
    let blobUrl;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}${src}`, { credentials: 'include' });
        if (!res.ok) throw new Error(res.statusText);
        const blob = await res.blob();
        if (revoked) return;
        blobUrl = URL.createObjectURL(blob);
        setUrl(blobUrl);
      } catch {
        setError(true);
      }
    })();
    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [src]);

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 text-gray-400 ${className}`}>
        <ImageIcon className="w-5 h-5" />
      </div>
    );
  }
  if (!url) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`}>
        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
      </div>
    );
  }
  return <img src={url} alt={alt} className={`object-cover ${className}`} />;
};

// ────────────────────────────────────────────────────────────────────────────
// Detail drawer
// ────────────────────────────────────────────────────────────────────────────
const ConsultationDrawer = ({ item, onClose, onChanged, onDeleted }) => {
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/contacts/${item.id}/status`,
        { status },
        withAdminAuth()
      );
      onChanged();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const downloadPhoto = async () => {
    if (!item.photo_url) return;
    try {
      const res = await fetch(`${BACKEND_URL}${item.photo_url}`, { credentials: 'include' });
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = item.photo_meta?.filename || 'room-photo.jpg';
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    } catch (e) {
      alert(e.message);
    }
  };

  const replyByEmail = async () => {
    const firstName = (item.name || '').split(' ')[0] || '';
    const subject = `Re: Your art consultation enquiry — Zebra One Gallery`;
    const body =
`Dear ${firstName},

Thank you so much for sharing your room with us — it's a real pleasure to look over your space.

I've been studying your photograph${item.photo_url ? '' : ' and notes'}, and I'm putting together a small selection of works from our collection that I believe will sit beautifully alongside your light, palette and the character of the room. I'll be in touch within 24 hours with a personal shortlist and a few thoughts on placement and scale.

If anything has changed since you sent this — a wall you've already committed to, a budget guide, a piece you've fallen for elsewhere — do let me know. The more specific the brief, the better the curation.

Looking forward to it.

Warmly,
Gabrielle du Plooy
Founder · Zebra One Gallery
Hampstead, London
www.zebraonegallery.com
`;

    // Open the email client first (must be a direct user-gesture call)
    window.location.href =
      `mailto:${encodeURIComponent(item.email)}` +
      `?subject=${encodeURIComponent(subject)}` +
      `&body=${encodeURIComponent(body)}`;

    // Then auto-advance status to "replied" (best-effort, don't block UX on failure)
    if ((item.status || 'new') !== 'replied') {
      try {
        await axios.patch(
          `${BACKEND_URL}/api/admin/contacts/${item.id}/status`,
          { status: 'replied' },
          withAdminAuth()
        );
        onChanged();
      } catch (err) {
        // Non-fatal — admin already opened the mailto/whatsapp link.
        // List refreshes on close so state still converges.
        if (process.env.NODE_ENV !== 'production') {
          console.debug('[consultations] status patch failed:', err?.response?.status, err?.message);
        }
      }
    }
  };

  const deleteRow = async () => {
    const label = `${item.name}${item.email ? ` (${item.email})` : ''}`;
    if (!window.confirm(
      `Delete this consultation enquiry from ${label}?\n\n` +
      `This permanently removes the row and (if attached) the room photograph. ` +
      `This cannot be undone.`,
    )) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/consultations/${item.id}`,
        withAdminAuth(),
      );
      onDeleted();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
      setDeleting(false);
    }
  };


  return (
    <motion.div
      initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
      transition={{ type: 'tween', duration: 0.3 }}
      className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-2xl z-50 overflow-y-auto"
      data-testid="consultation-drawer"
    >
      <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.25em] text-gray-500">Consultation request</p>
          <h2 className="font-serif text-2xl truncate">{item.name}</h2>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded" data-testid="consultation-drawer-close">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="p-6 space-y-6">
        {/* Primary action — pre-composed reply */}
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={replyByEmail}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-black text-white text-xs uppercase tracking-[0.2em] hover:bg-neutral-800 transition-colors"
            data-testid="consultation-reply-btn"
          >
            <Send className="w-4 h-4" /> Reply to {(item.name || '').split(' ')[0] || 'client'}
          </button>
          <p className="text-xs text-gray-500">
            Opens your email client with a pre-composed message from Gabrielle.
          </p>
        </div>

        {/* Photo */}
        {item.photo_url ? (
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500">Room photograph</p>
              <button
                onClick={downloadPhoto}
                className="text-xs flex items-center gap-1.5 text-gray-600 hover:text-black"
                data-testid="consultation-photo-download"
              >
                <Download className="w-3.5 h-3.5" /> Download original
              </button>
            </div>
            <PrivatePhoto
              src={item.photo_url}
              alt={`Room from ${item.name}`}
              className="w-full max-h-[500px] bg-gray-50 rounded"
            />
            {item.photo_meta?.size_bytes && (
              <p className="text-xs text-gray-500 mt-2">
                {item.photo_meta.filename} · {(item.photo_meta.size_bytes / 1024 / 1024).toFixed(2)}MB
              </p>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-3 text-sm text-gray-500 border border-dashed border-gray-200 p-4">
            <Camera className="w-5 h-5" /> Client did not include a room photograph.
          </div>
        )}

        {/* Contact */}
        <div className="grid sm:grid-cols-2 gap-3">
          <a href={`mailto:${item.email}`} className="flex items-center gap-2 text-sm text-gray-700 hover:text-black p-3 border rounded">
            <Mail className="w-4 h-4 text-gray-400" /> {item.email}
          </a>
          {item.phone && (
            <a href={`tel:${item.phone}`} className="flex items-center gap-2 text-sm text-gray-700 hover:text-black p-3 border rounded">
              <Phone className="w-4 h-4 text-gray-400" /> {item.phone}
            </a>
          )}
        </div>

        {/* Style + Room */}
        <div className="grid grid-cols-1 gap-4">
          {item.style && (
            <div className="border-l-2 border-black pl-4">
              <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-1">Preferred style</p>
              <p className="text-sm">{item.style}</p>
            </div>
          )}
          {item.room && (
            <div className="border-l-2 border-black pl-4">
              <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-1">Room description</p>
              <p className="text-sm whitespace-pre-line leading-relaxed">{item.room}</p>
            </div>
          )}
        </div>

        {/* Submitted */}
        <div className="text-xs text-gray-500 pt-3 border-t">
          Submitted {fmtDate(item.createdAt)}
        </div>

        {/* Status actions */}
        <div className="border-t pt-5">
          <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-3">Mark as</p>
          <div className="flex flex-wrap gap-2">
            {['new', 'replied', 'closed'].map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                disabled={busy || (item.status || 'new') === s}
                data-testid={`consultation-mark-${s}`}
                className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition ${
                  (item.status || 'new') === s
                    ? 'bg-black text-white border-black'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-black'
                } disabled:opacity-50`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Danger zone — hard delete */}
        <div className="border-t pt-5 mt-2">
          <p className="text-[11px] uppercase tracking-[0.2em] text-gray-400 mb-3">
            Housekeeping
          </p>
          <button
            onClick={deleteRow}
            disabled={deleting}
            data-testid="consultation-delete-btn"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.2em] text-red-600 border border-red-200 hover:bg-red-50 hover:border-red-300 transition disabled:opacity-50"
          >
            {deleting ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Deleting…
              </>
            ) : (
              <>
                <Trash2 className="w-3.5 h-3.5" /> Delete this consultation
              </>
            )}
          </button>
          <p className="text-xs text-gray-400 mt-2">
            Removes the row and any attached room photograph. Use for test or spam enquiries.
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Row
// ────────────────────────────────────────────────────────────────────────────
const ConsultationRow = ({ item, onOpen }) => {
  const status = item.status || 'new';
  return (
    <button
      onClick={onOpen}
      className="w-full text-left bg-white border border-gray-200 hover:border-black hover:shadow-sm transition p-4 flex gap-4 items-center"
      data-testid="consultation-row"
    >
      {item.photo_url ? (
        <PrivatePhoto
          src={item.photo_url}
          alt={item.name}
          className="w-20 h-20 flex-shrink-0 bg-gray-50 rounded"
        />
      ) : (
        <div className="w-20 h-20 flex-shrink-0 bg-gray-50 rounded flex items-center justify-center text-gray-300">
          <Camera className="w-6 h-6" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <h3 className="font-medium truncate">{item.name}</h3>
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${STATUS_BADGE[status] || STATUS_BADGE.new}`}>
            {status}
          </span>
        </div>
        <p className="text-sm text-gray-600 truncate">{item.email}</p>
        <p className="text-xs text-gray-500 mt-1">
          {item.style ? `${item.style} · ` : ''}{fmtDate(item.createdAt)}
        </p>
      </div>
    </button>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Main page
// ────────────────────────────────────────────────────────────────────────────
export default function ConsultationsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('new');
  const [active, setActive] = useState(null);

  const fetchItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/consultations`, withAdminAuth());
      setItems(r.data?.consultations || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const visible = items.filter((c) =>
    statusFilter === 'all' ? true : (c.status || 'new') === statusFilter
  );

  return (
    <div className="p-6 md:p-8 max-w-6xl" data-testid="consultations-page">
      <div className="mb-8 flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-light tracking-tight">Consultations</h1>
          <p className="text-gray-500 mt-2">
            Free art-consultation enquiries with optional room photographs.
          </p>
        </div>
        <div className="flex gap-1">
          {STATUS_FILTERS.map((f) => {
            const n = items.filter((c) =>
              f.key === 'all' ? true : (c.status || 'new') === f.key
            ).length;
            return (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                data-testid={`consultations-filter-${f.key}`}
                className={`px-3 py-1.5 text-sm rounded border transition ${
                  statusFilter === f.key
                    ? 'bg-black text-white border-black'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-black'
                }`}
              >
                {f.label} <span className="opacity-60">({n})</span>
              </button>
            );
          })}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 flex gap-2 text-sm text-red-700" data-testid="consultations-error">
          <AlertCircle className="w-4 h-4 mt-0.5" /> {error}
        </div>
      )}

      {loading ? (
        <div className="text-gray-500 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : visible.length === 0 ? (
        <div className="p-12 rounded bg-gray-50 border border-gray-200 text-center text-gray-500" data-testid="consultations-empty">
          <Inbox className="w-8 h-8 mx-auto mb-3 text-gray-400" />
          <p className="font-medium text-gray-700 mb-1">No consultations in this view</p>
          <p className="text-xs">Room-photo enquiries from the public site will appear here.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((c) => (
            <ConsultationRow key={c.id} item={c} onOpen={() => setActive(c)} />
          ))}
        </div>
      )}

      <AnimatePresence>
        {active && (
          <ConsultationDrawer
            item={active}
            onClose={() => setActive(null)}
            onChanged={() => { setActive(null); fetchItems(); }}
            onDeleted={() => { setActive(null); fetchItems(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
